import { useCallback, useEffect, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { celebrate } from "@/lib/celebrate";
import { ProgressPanel } from "@/components/ProgressPanel";
import { GardenHeader, GardenCard } from "@/components/client/Garden";
import { GardenPostcard } from "@/components/client/GardenPostcard";
import { GardenMilestone } from "@/components/client/GardenMilestone";
import { RewardModal, TodayRewards } from "@/components/client/RewardModal";
import { useAuth } from "@/context/AuthContext";
import { ReflectionInvite } from "@/components/ReflectionPrompt";
import { ReflectionInsights } from "@/components/ReflectionInsights";
import { StrategyCard } from "@/components/client/StrategyCard";
import { WeeklyDigest } from "@/components/WeeklyDigest";
import { WeeklyWinsCard } from "@/components/client/WeeklyWinsCard";
import { DashboardTabs } from "@/components/client/DashboardTabs";
import { HabitRow } from "@/components/client/HabitRow";
import { HabitGrid } from "@/components/client/HabitGrid";
import { WellbeingSection } from "@/components/client/WellbeingSection";
import { localToday } from "@/lib/habitColors";
import { RemindersPanel } from "@/components/RemindersPanel";
import { AlarmOverlay } from "@/components/AlarmOverlay";
import { TrackerManager } from "@/components/TrackerManager";
import { WellbeingTrends } from "@/components/WellbeingTrends";
import { PatternInsights } from "@/components/PatternInsights";
import { WelcomeTour } from "@/components/WelcomeTour";
import { TimezonePrompt } from "@/components/TimezonePrompt";
import { NudgeCard } from "@/components/NudgeCard";
import { BuddyCheers } from "@/components/BuddyCheers";
import { RainDayBanner } from "@/components/community/RainDayBanner";
import { WeekRecapLine } from "@/components/client/WeekRecapLine";
import { MilestoneCelebration, MilestoneNoteCard, MilestonesSection } from "@/components/client/Milestones";
import { StageJourneyCard } from "@/components/client/StageTimeline";
import { HabitForm, HabitCard, ThankYouModal } from "@/components/client/HabitParts";
import { KeystoneSpotlight } from "@/components/client/KeystoneSpotlight";
import { RewardsSection, ClinicianNote } from "@/components/client/RewardParts";
import { WellbeingCheckinForm, RecentCheckins } from "@/components/client/WellbeingParts";
import { Plus, BookHeart, Compass } from "@/lib/icons";
import { TodayBar, ManageHabitsPanel } from "@/components/client/HabitsHeader";

const POSITIVE_WORDS = [
  "You showed up. That's the whole game.",
  "Small choice, big ripple.",
  "That's your future self smiling.",
  "One brick added to the foundation.",
  "Progress, not perfection. And this counts.",
];
const TOAST_MS = { short: 4000, long: 6000, extended: 7000 };
const EMPTY_HABIT = { title: "", description: "", kind: "start", category: "other", final_goal: 7, rubric: null, rubric_source: "client", color: "amber", icon: null, taper_start: null };

const announce = (achievements = []) => {
  achievements.forEach((a) => toast.success(`Achievement unlocked: ${a.title} (+${a.points})`, { duration: 5000 }));
};

export default function ClientDashboard() {
  const [habits, setHabits] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [symptoms, setSymptoms] = useState([]);
  const [recs, setRecs] = useState([]);
  const [game, setGame] = useState(null);
  const { user } = useAuth();
  const [rewardFor, setRewardFor] = useState(null);
  const [digest, setDigest] = useState(null);
  const [reminderPreset, setReminderPreset] = useState(null);
  const [reflections, setReflections] = useState({ pending: [], history: [], insights: null, reasons: [] });
  const [milestones, setMilestones] = useState(null);
  const loadMilestones = () => api.get("/milestones/me").then((r) => setMilestones(r.data)).catch(() => {});
  const [newHabit, setNewHabit] = useState(EMPTY_HABIT);
  const [showHabitForm, setShowHabitForm] = useState(false);
  const [manage, setManage] = useState(false);
  const [scaleDefs, setScaleDefs] = useState([]);
  const [scales, setScales] = useState({});
  const [trackers, setTrackers] = useState([]);
  const [trackerVals, setTrackerVals] = useState({});
  const [thankYou, setThankYou] = useState(null);
  const [tagLibrary, setTagLibrary] = useState([]);
  const [generatingFor, setGeneratingFor] = useState(null);
  const [aiStatus, setAiStatus] = useState(null);
  const [tab, setTab] = useState(() => sessionStorage.getItem("hc_tab") || "today");
  useEffect(() => { sessionStorage.setItem("hc_tab", tab); }, [tab]);
  const [skippedReflections, setSkippedReflections] = useState(() => JSON.parse(sessionStorage.getItem("hc_reflections_skipped") || "[]"));
  const skipReflection = (id) => setSkippedReflections((s) => { const n = [...s, id]; sessionStorage.setItem("hc_reflections_skipped", JSON.stringify(n)); return n; });
  const pendingReflections = reflections.pending.filter((r) => !skippedReflections.includes(r.id));

  const load = async () => {
    const [h, r, s, rc, g, rf, dg, tr, ais] = await Promise.all([
      api.get("/habits"), api.get("/rewards"), api.get("/symptoms"), api.get("/recommendations"),
      api.get("/gamification/me"), api.get("/reflections"), api.get("/digest/weekly"), api.get("/trackers"), api.get("/ai/status"),
    ]);
    setHabits(h.data); setRewards(r.data); setSymptoms(s.data); setRecs(rc.data);
    setGame(g.data); setReflections(rf.data); setDigest(dg.data); setTrackers(tr.data); setAiStatus(ais.data);
    loadMilestones();
  };

  useEffect(() => {
    load();
    api.get("/symptoms/tags").then((r) => {
      setTagLibrary(r.data.tags);
      setScaleDefs(r.data.scales);
      setScales(Object.fromEntries(r.data.scales.map((d) => [d.key, d.default])));
    });
  }, []);

  const clearReminderPreset = useCallback(() => setReminderPreset(null), []);

  const addHabit = async (e) => {
    e.preventDefault();
    if (!newHabit.title.trim()) return;
    const staged = newHabit.measure_mode === "stages" && newHabit.stages?.length;
    if (staged && newHabit.stages.some((s) => !s.name.trim())) return toast.error("Give every stage a name");
    const { measure_mode, stages, ...rest } = newHabit;
    const body = { ...rest, rubric: !staged && rest.rubric && rest.rubric.type !== "yesno" ? { ...rest.rubric, why: undefined } : null };
    try {
      const created = await api.post("/habits", body);
      if (staged) await api.put(`/habits/${created.data.id}/stages`, { stages: stages.map((s) => ({ ...s, name: s.name.trim() })), stage_index: 0 });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't add that habit");
      return;
    }
    setNewHabit(EMPTY_HABIT);
    setShowHabitForm(false);
    toast.success("New habit added. You already took the first step.");
    load();
  };

  const checkIn = async (habit, value, tier) => {
    let res;
    try {
      res = await api.post("/habits/checkin", { habit_id: habit.id, value, tier });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't log that");
      return;
    }
    if (res.data.already) {
      toast("Already logged today — rest counts too.");
    } else if (res.data.milestone) {
      load();  // the ceremony comes first; the usual toasts can wait
      return;
    } else {
      toast.success(`${POSITIVE_WORDS[Math.floor(Math.random() * POSITIVE_WORDS.length)]} +${res.data.points_earned} pts`);
      if (res.data.log?.tier === "full" || !res.data.log?.tier) setTimeout(() => setRewardFor(habit), 400);
      if (res.data.checkin_mode_label) toast(res.data.checkin_mode_label, { duration: TOAST_MS.short });
      if (res.data.resilience) toast.success(res.data.resilience.label, { duration: TOAST_MS.extended, icon: "🛡️" });
      if (res.data.level_up?.advanced) {
        celebrate(true);
        toast.success(`You beat your goal! "${habit.title}" moves up to ${res.data.level_up.new_goal_label.toLowerCase()}.`, { duration: TOAST_MS.long });
      } else {
        celebrate(false);
      }
      announce(res.data.achievements);
    }
    load();
  };

  const answerProposal = async (habit, proposal, accept) => {
    await api.post(`/habits/${habit.id}/rubric/proposals/${proposal.id}`, { accept });
    toast.success(accept ? `"${habit.title}" is now measured the way you and ${proposal.clinician_name} agreed.` : "Kept your own way of measuring it.");
    load();
  };

  const undoCheckIn = async (habit) => {
    const res = await api.delete(`/habits/${habit.id}/checkin`);
    toast(res.data.points_reversed ? `Unmarked "${habit.title}" — ${res.data.points_reversed} pts returned to the pool.` : `Unmarked "${habit.title}".`);
    load();
  };

  const levelUp = async (habit, accept) => {
    const res = await api.post(`/habits/${habit.id}/level-up`, { accept });
    if (res.data.advanced) {
      celebrate(true);
      toast.success(`Level up! "${habit.title}" is now ${res.data.new_goal_label.toLowerCase()}. +50 pts`, { duration: TOAST_MS.long });
      announce(res.data.achievements);
    } else {
      toast("Staying here a little longer. That's wisdom, not hesitation.");
    }
    load();
  };

  const answerReflection = async (id, reason, detail, useShield = false) => {
    let res;
    try {
      res = await api.post(`/reflections/${id}`, { reason, detail, use_shield: useShield });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save that reflection — we'll ask again next time.");
      throw err;
    }
    toast.success(`Thank you for your honesty. +${res.data.points_earned} pts`);
    if (res.data.shield_used) {
      celebrate(false);
      toast.success("Shield used — your goal and streak are protected.", { duration: 5000 });
    }
    announce(res.data.achievements);
    load();
  };

  const setFocus = async (habit, focus) => {
    await api.patch(`/habits/${habit.id}/focus`, { focus });
    toast.success(focus === "keystone" ? `"${habit.title}" is now your Keystone habit — bigger rewards, full attention.` : `"${habit.title}" set to ${focus}.`);
    load();
  };

  const removeHabit = async (id) => {
    await api.delete(`/habits/${id}`);
    toast("Habit archived. No judgment, no punishment.");
    load();
  };

  const generateReward = (habit) => setRewardFor(habit);

  const favorite = async (id) => {
    const r = await api.post(`/rewards/${id}/favorite`);
    setRewards((rs) => rs.map((x) => (x.id === id ? { ...x, favorite: r.data.favorite } : x)));
    toast.success(r.data.favorite ? "Starred — it may come back on a future win." : "Removed from favourites.");
  };
  const deleteReward = async (id) => {
    try {
      await api.delete(`/rewards/${id}`);
      setRewards((rs) => rs.filter((r) => r.id !== id));
      toast("Reward removed.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't remove that reward."); }
  };
  const claim = async (id) => {
    const r = await api.post(`/rewards/claim/${id}`);
    toast.success(r.data.message || "Claimed. Enjoy it fully.");
    load();
  };

  const feedback = async (id, fb) => {
    await api.post("/rewards/feedback", { reward_id: id, feedback: fb });
    toast("Thanks — this helps us pick better rewards for you.");
    load();
  };

  const submitSymptoms = async (extraFields) => {
    const { mood, anxiety, ...extra } = scales;
    const tracker_values = Object.fromEntries(Object.entries(trackerVals).filter(([, v]) => v !== undefined));
    const res = await api.post("/symptoms", { mood, anxiety, scales: extra, tracker_values, ...extraFields });
    toast.success("Logged. Thanks for checking in with yourself.");
    if (res.data.thank_you) {
      celebrate(true);
      setThankYou(res.data.thank_you);
    }
    announce(res.data.achievements);
    setTrackerVals({});
    load();
    return true;
  };

  const createTracker = async (body) => {
    await api.post("/trackers", body);
    toast.success(`Tracking "${body.name}" from now on.`);
    load();
  };

  const archiveTracker = async (id) => {
    await api.delete(`/trackers/${id}`);
    toast("Tracker archived. Past entries are kept.");
    load();
  };

  const pendingCount = habits.filter((h) => !h.completed_today).length;
  const checkedInToday = symptoms.some((e) => e.date === localToday());
  const doneToday = habits.length - pendingCount;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 space-y-8 relative z-10">
      <WelcomeTour />
      <TimezonePrompt />
      <ThankYouModal text={thankYou} onClose={() => setThankYou(null)} />
      {milestones?.celebration && !(milestones.celebration.demo && sessionStorage.getItem("rr_demo_celebrated")) && (
        <MilestoneCelebration milestone={milestones.celebration} game={game} onClose={() => { if (milestones.celebration.demo) sessionStorage.setItem("rr_demo_celebrated", "1"); setMilestones((m) => ({ ...m, celebration: null })); load(); }} />
      )}
      <AlarmOverlay onCheckIn={(habitId) => { const h = habits.find((x) => x.id === habitId); if (h) checkIn(h); }} />

      <GardenMilestone game={game} user={user} />
      <GardenHeader game={game} onOpen={() => setTab("progress")} />
      <DashboardTabs value={tab} onChange={setTab} badges={{ today: pendingCount, rewards: rewards.filter((r) => !r.claimed && !r.withdrawn).length }} />

      {tab === "today" && (
        <div className="space-y-5" data-testid="tab-panel-today">
          <WeekRecapLine />
          {milestones?.notes?.map((n) => <MilestoneNoteCard key={n.id} note={n} onDismiss={() => api.patch(`/milestones/${n.id}`, { note_dismissed: true }).then(loadMilestones)} />)}
          <RainDayBanner compact />
          <NudgeCard />
          <BuddyCheers onWaterBack={() => api.get("/gamification/me").then((g) => setGame(g.data)).catch(() => {})} />
          {rewardFor && <RewardModal habit={rewardFor} onClose={() => setRewardFor(null)} onChanged={load} />}
          <WellbeingSection checkedInToday={checkedInToday}>
            <WellbeingCheckinForm scaleDefs={scaleDefs} scales={scales} onScales={setScales} trackers={trackers}
              trackerVals={trackerVals} onTrackerVals={setTrackerVals} tagLibrary={tagLibrary} onSubmit={submitSymptoms} />
          </WellbeingSection>

          <TodayBar pendingCount={pendingCount} showForm={showHabitForm} manage={manage}
            onAdd={() => { setShowHabitForm((v) => !v); setManage(false); }} onManage={() => { setManage((v) => !v); setShowHabitForm(false); }}>
            <ClinicianNote rec={recs[0]} habits={habits} onSetReminder={setReminderPreset} />
          </TodayBar>
          <AnimatePresence>{showHabitForm && <HabitForm value={newHabit} onChange={setNewHabit} onSubmit={addHabit} />}</AnimatePresence>
          {manage && <ManageHabitsPanel habits={habits} onRemove={removeHabit} onFocus={setFocus} />}

          <div data-testid="habits-column" className="space-y-3">
            <div className="flex items-baseline justify-between gap-3">
              <h2 className="font-display text-2xl font-bold text-stone-900">Which habits did you do today?</h2>
              <span className="text-xs text-stone-500">{doneToday}/{habits.length} marked</span>
            </div>
            <KeystoneSpotlight habits={habits} onCheckIn={checkIn} onUndo={undoCheckIn} onFocus={setFocus} />
            {habits.length === 0 && !showHabitForm && (
              <div className="warm-card p-10 text-center">
                <BookHeart className="w-10 h-10 text-amber-700 mx-auto mb-3" />
                <p className="text-stone-600">Start with one gentle intention. Even one is enough.</p>
                <button data-testid="empty-add-habit-btn" onClick={() => setShowHabitForm(true)} className="amber-btn text-sm mt-4 inline-flex items-center gap-1.5"><Plus className="w-4 h-4" /> Add your first habit</button>
              </div>
            )}
            {habits.filter((h) => h.focus !== "keystone").map((h) => (
              <HabitRow key={h.id} h={h} cardProps={{ generating: generatingFor === h.id, onCheckIn: checkIn, onUndo: undoCheckIn, onLevelUp: levelUp, onReward: generateReward,
                onRemind: setReminderPreset, onRemove: removeHabit, onChange: load, onFocus: setFocus, onProposal: answerProposal }} />
            ))}
            {habits.filter((h) => h.focus === "keystone").map((h) => (
              <details key={h.id} className="rounded-2xl border border-stone-200 bg-white px-4 py-3" data-testid={`keystone-details-${h.id}`}>
                <summary className="text-sm font-semibold text-stone-700 cursor-pointer">Keystone details · ladder, rubric, reminders, rewards</summary>
                <div className="mt-3"><HabitCard h={h} embedded generating={generatingFor === h.id} onCheckIn={checkIn} onUndo={undoCheckIn} onLevelUp={levelUp} onReward={generateReward}
                  onRemind={setReminderPreset} onRemove={removeHabit} onChange={load} onFocus={setFocus} onProposal={answerProposal} /></div>
              </details>
            ))}
          </div>
          <TodayRewards rewards={rewards} habits={habits} onClaim={claim} onGet={(h) => setRewardFor(h)} generatingFor={generatingFor} />
          <div id="reminders">
            <RemindersPanel habits={habits} presetHabitId={reminderPreset} onPresetConsumed={clearReminderPreset} />
          </div>
        </div>
      )}

      {tab === "grid" && <div data-testid="tab-panel-grid"><HabitGrid onChanged={load} /></div>}

      {tab === "progress" && (
        <div className="space-y-6" data-testid="tab-panel-progress">
          <GardenCard game={game} />
          <GardenPostcard game={game} />
          <MilestonesSection data={milestones} game={game} onChange={loadMilestones} />
          <StageJourneyCard key={habits.map((h) => `${h.id}:${h.stage?.index}`).join("|")} />
          <ProgressPanel data={game} />
          <WeeklyDigest digest={digest} onChange={load} />
          <WeeklyWinsCard />
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="space-y-6">
              <WellbeingTrends symptoms={symptoms} defs={scaleDefs} trackers={trackers} title="Your trends" height={200} />
              <RecentCheckins symptoms={symptoms} scaleDefs={scaleDefs} />
            </div>
            <div className="space-y-6">
              <PatternInsights />
              <TrackerManager trackers={trackers} onCreate={createTracker} onArchive={archiveTracker}
                subtitle="Anything you want to watch alongside your habits. Your coach can add trackers here too." />
              <StrategyCard onChanged={load} />
              <div className="warm-card p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Compass className="w-5 h-5 text-amber-700" />
                  <h3 className="font-display text-lg font-bold text-stone-900">What gets in the way</h3>
                </div>
                <ReflectionInsights insights={reflections.insights} history={reflections.history} />
                {pendingReflections.length > 0 && (
                  <div className="mt-4 space-y-2" data-testid="reflection-invites">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-stone-500">Only if useful to you</p>
                    {pendingReflections.map((r) => (
                      <ReflectionInvite key={r.id} reflection={r} reasons={reflections.reasons} shields={reflections.shields || 0} onSubmit={answerReflection} onDismiss={() => skipReflection(r.id)} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === "rewards" && (
        <div data-testid="tab-panel-rewards">
          <RewardsSection rewards={rewards} onClaim={claim} onFeedback={feedback} onFavorite={favorite} onDelete={deleteReward} aiStatus={aiStatus} />
        </div>
      )}
    </div>
  );
}
